import { useState } from 'react';

import {
    ComposableMap,
    ZoomableGroup,
    Geographies,
    Geography,
    Marker,
} from 'react-simple-maps';
import ReactTooltip from 'react-tooltip';

import { MarkerIcon } from 'components/organisms/InteractiveMap/MarkerIcon';
import { projectionConfig } from 'constants/map/config';
import {
    ClickEventHandler,
    CtxMenuEventHandler,
    MarkerClickEventHandler,
    MarkerData,
    TopoJSON,
} from 'types/map';

import { MapContainer } from './index.styled';

type MapComponentProps = {
    data: TopoJSON;
    hidden?: number[];
    markers?: MarkerData[];
    selected?: number[];
    onCtxMenu?: CtxMenuEventHandler;
    onClick?: ClickEventHandler;
    onMarkerClick?: MarkerClickEventHandler;
};

export const MapComponent = ({
    data,
    hidden = [],
    selected = [],
    markers = [],
    onCtxMenu = () => undefined,
    onClick = () => undefined,
    onMarkerClick = () => undefined,
}: MapComponentProps): JSX.Element => {
    const [tooltipContent, setTooltipContent] = useState('');

    const [scale, setScale] = useState<number>(0.5);

    return (
        <MapContainer>
            <ComposableMap
                projection="geoAlbers"
                projectionConfig={projectionConfig}
                data-tip=""
            >
                <ZoomableGroup
                    zoom={1}
                    minZoom={0.5}
                    maxZoom={4}
                    center={[90, 65]}
                    onMove={(props, e) => {
                        setScale(e.transform.k);
                    }}
                >
                    <Geographies geography={data}>
                        {({ geographies }) =>
                            geographies.map((geo) => {
                                const isHidden = hidden.includes(
                                    geo.properties.id
                                );
                                const isSelected = selected.includes(
                                    geo.properties.id
                                );

                                const classNames = [
                                    'hasHref',
                                    isHidden && 'hidden',
                                    isSelected && 'selected',
                                ]
                                    .filter((e) => !!e)
                                    .join(' ');

                                return (
                                    <Geography
                                        className={classNames}
                                        key={geo.rsmKey}
                                        geography={geo}
                                        fill={
                                            isHidden
                                                ? '#5c5c5c'
                                                : geo.properties.color
                                        }
                                        onClick={(e) =>
                                            onClick(e, geo.properties)
                                        }
                                        onContextMenu={(evt) => {
                                            onCtxMenu(evt, geo.properties);
                                        }}
                                        onMouseEnter={
                                            isHidden
                                                ? () => undefined
                                                : () => {
                                                      setTooltipContent(
                                                          geo.properties.name
                                                      );
                                                  }
                                        }
                                        onMouseLeave={
                                            isHidden
                                                ? () => undefined
                                                : () => {
                                                      setTooltipContent('');
                                                  }
                                        }
                                    />
                                );
                            })
                        }
                    </Geographies>
                    {markers.map((el) => (
                        <Marker
                            coordinates={el.coords}
                            key={el.id}
                            onClick={(e) => onMarkerClick(e, el)}
                            onMouseEnter={() => {
                                setTooltipContent(el.name || '');
                            }}
                            onMouseLeave={() => {
                                setTooltipContent('');
                            }}
                        >
                            <MarkerIcon
                                color={el.color}
                                image={el.imageUuid}
                                link={el.hrefUuid}
                                scale={1 / scale}
                            />
                        </Marker>
                    ))}
                </ZoomableGroup>
            </ComposableMap>
            <ReactTooltip
                className="react-tooltip"
                backgroundColor="white"
                arrowColor="white"
                textColor="inherit"
            >
                {tooltipContent}
            </ReactTooltip>
        </MapContainer>
    );
};
